import _ from 'underscore';
import { Formatter } from '@bingads-webui/listener';
import { getParameter, updateParameter } from '@bingads-webui/url-util';
import { getUtcTime } from '@bingads-webui-universal/primitive-utilities';
import { safeParseInteger } from './format-utilities';
import { getErrorDetails } from './error-details';
import type { BasicFormatterOptions, InstrumentationLog } from '../types';

export class BasicFormatter extends Formatter {
  applicationSubSystem?: string;
  applicationSystem?: string;
  constructor(options: BasicFormatterOptions = {}) {
    super();
    this.applicationSystem = options.applicationSystem;
    this.applicationSubSystem = options.applicationSubSystem;
  }

  getPFlagsProps(rawLog: InstrumentationLog) {
    let pflags = '';

    if (_.isString(rawLog.api)) {
      let apiString = rawLog.api.trim();
      // hide all token / code related values due to privacy issue.
      if (getParameter('authCode', apiString)) {
        apiString = updateParameter('authCode', 'authCodePlaceholder', apiString);
      } else if (getParameter('accessToken', apiString)) {
        apiString = updateParameter('accessToken', 'accessTokenPlaceholder', apiString);
      } else if (getParameter('refreshToken', apiString)) {
        apiString = updateParameter('refreshToken', 'refreshTokenPlaceholder', apiString);
      } else if (getParameter('LongLivedAccessToken', apiString)) {
        apiString = updateParameter('LongLivedAccessToken', 'LongLivedAccessTokenPlaceholder', apiString);
      }
      pflags += `rUrl:${apiString};`;
    }
    if (rawLog.parentScenarioName && rawLog.parentScenarioId) {
      pflags += `pScenario:${rawLog.parentScenarioName};pScenarioId:${rawLog.parentScenarioId};`;
    }
    if (rawLog.IsAksService) {
      pflags += 'IsAksService:true;';
    }
    if (rawLog.IsNetCoreService) {
      pflags += 'IsNetCoreService:true;';
    }
    if (rawLog.IsFlightBuildServed) {
      // Emitted only when the server actually served the flight (rspack) build for the resolved locale.
      // Serve-scoped and true-only: apps that never serve a flight build (e.g. PMax Lite landing, which
      // always serves webpack) never emit this, regardless of the user-scoped FlightBuildEnabled pilot flag.
      pflags += 'FlightBuild:true;';
    }
    if (rawLog.IsSSRHydration) {
      pflags += 'IsSSRHydration:true;';
    }
    return pflags;
  }

  getEventType(rawLog: InstrumentationLog) {
    if (rawLog.message && JSON.stringify(rawLog.message).indexOf('perf-marker@') !== -1) {
      return 'perfmarker';
    }
    return rawLog.entryType;
  }

  format(rawLog: InstrumentationLog) {
    const log = {
      Sn: rawLog.scenarioName || '',
      SceID: rawLog.scenarioId || '',
      RID: rawLog.requestId || '',
      Api: _.isString(rawLog.api) ? rawLog.api.trim().toLowerCase() : '',
      Pass: _.isUndefined(rawLog.pass) ? '' : rawLog.pass,
      Dur: safeParseInteger(rawLog.timeTaken),
      Ime: _.isUndefined(rawLog.isMethodEnter) ? '' : rawLog.isMethodEnter,
      Hm: rawLog.httpMethod || '',
      Timestamp: rawLog.timeStamp || getUtcTime(),
      SesID: rawLog.sessionId || '',
      Url: (window.location.pathname || '') + (window.location.search || '') + (window.location.hash || ''),
      UID: rawLog.userId || '',
      CID: rawLog.customerId || '',
      AID: rawLog.accountId || '',
      LCID: rawLog.lcid || '',
      Mes: JSON.stringify(rawLog.message),
      Lc: _.isArray(rawLog.logCategory) ?
        rawLog.logCategory.join(',') : rawLog.logCategory,
      Ll: rawLog.logLevel ? (rawLog.logLevel as { name?: string }).name : '',
      Env: window.location.hostname,
      As: this.applicationSystem || '',
      Asub: this.applicationSubSystem || '',
      An: rawLog.activityName || '',
      ActID: rawLog.activityId || '',
      Et: this.getEventType(rawLog) || '',
      Loc: rawLog.market || '',
      Role: rawLog.userRole || '',
      MgdBy: rawLog.managedBy || '',
      PFlags: this.getPFlagsProps(rawLog) || '',
      Iu: rawLog.impactUser || null,
      Ec: safeParseInteger(rawLog.entityCount),
      UserAgent: (window.navigator || {}).userAgent || '',
      PgID: rawLog.pageTrackingId || '',
      PgVer: rawLog.pageVersion || '',
      DbgMode: rawLog.isDebugMode || false,
    };

    // Source/Line/Col/Stack/FailKind are additive: they are declared on the log tables but were
    // never populated, so nothing downstream can depend on their previous (empty) value.
    return Object.assign(log, getErrorDetails(rawLog));
  }
}
